import { MessageSquare } from "lucide-react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";

const PROJECT_TYPE_LABELS = {
  loft_conversion: "Loft Conversion",
  kitchen_renovation: "Kitchen Renovation",
  house_extension: "House Extension",
  fitted_wardrobes: "Fitted Wardrobes",
  full_refurbishment: "Full Refurbishment"
};

const STATUS_CLASSES = {
  pending: "status-pending",
  contacted: "status-contacted",
  quoted: "status-quoted",
  completed: "status-completed",
  cancelled: "status-cancelled"
};

function formatProjectType(type) {
  return PROJECT_TYPE_LABELS[type] || type;
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function QuotesTable({ quotes, formatCurrency, onStatusUpdate }) {
  return (
    <div className="bg-white rounded-lg shadow-xl shadow-stone-200/50 overflow-hidden">
      <div className="px-6 py-4 border-b border-stone-100">
        <h2 className="text-xl font-bold text-slate-900">Quote Requests</h2>
      </div>

      {quotes.length === 0 ? (
        <div className="p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 rounded-full mb-4">
            <MessageSquare className="text-stone-400" size={28} />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No quote requests yet</h3>
          <p className="text-stone-500">New quote requests will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="quotes-table">
            <thead className="bg-stone-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Project</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Estimate</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {quotes.map((quote) => (
                <tr key={quote.id} className="table-row-hover" data-testid={`quote-row-${quote.id}`}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-slate-900">{quote.name}</p>
                      <p className="text-sm text-stone-500">{quote.email}</p>
                      <p className="text-sm text-stone-500">{quote.phone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-slate-900">{formatProjectType(quote.project_type)}</p>
                      <p className="text-sm text-stone-500 capitalize">
                        {quote.room_size} · {quote.quality_level} · {quote.complexity}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-bold text-slate-900">{formatCurrency(quote.estimated_cost)}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-stone-600">{formatDate(quote.created_at)}</p>
                  </td>
                  <td className="px-6 py-4">
                    <Select value={quote.status} onValueChange={(value) => onStatusUpdate(quote.id, value)}>
                      <SelectTrigger 
                        className={`w-32 h-8 text-xs font-semibold rounded-full ${STATUS_CLASSES[quote.status] || "status-pending"}`}
                        data-testid={`status-select-${quote.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="quoted">Quoted</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default QuotesTable;
